import { numberParam, resolveBounds, stringParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import {
  describeOfficial,
  judgeRule,
  missingRequirementReason,
  notChecked,
  plainResult,
  withRequirement,
} from './support.js';

/**
 * Head size check — the flagship measurement.
 *
 * Compares the estimated head height (top of the hair → chin) with the share of
 * the image height the document requires. Three things are kept apart in the
 * result:
 *
 *   official     — e.g. 50–69 % (State Department composition template);
 *   measurement  — our value plus its tolerance (head-top localisation);
 *   policy       — an internal bound that is NOT in the document (e.g. the
 *                  Russian regulation only sets a maximum, we add a minimum for
 *                  readability).
 *
 * A value inside the measurement tolerance is never reported as a hard failure,
 * and a reliability problem yields `uncertain` instead of a fabricated pass.
 */
export const headSizeCheck: ValidationCheck = {
  id: 'head_size',
  title: 'Размер головы',
  description: 'Доля высоты кадра от макушки (с волосами) до подбородка.',
  kind: 'geometry',
  requires: ['geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['geometry']);
    if (missing) return notChecked(ctx, headSizeCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) return notChecked(ctx, headSizeCheck, 'Геометрия недоступна.', ctx.rule.severity);

    const reference = stringParam(ctx.rule.params, 'reference', 'image_height');
    const value = geometry.headHeightRatio;
    const confidence = geometry.headTopConfidence.value;
    const tolerance = numberParam(ctx.rule.params, 'tolerance', ctx.rule.measurement?.tolerance ?? 0.01);

    const uncertainBelow = ctx.rule.measurement?.uncertainBelowConfidence ?? 0;
    /**
     * §6 of the calibration contract: when the top of the head cannot be
     * established with enough confidence, the answer is UNCERTAIN — never an
     * approximate number presented as a verdict. This is checked *before* the
     * comparison, because it is the measurement itself that is unreliable, not
     * the position of the value inside the official range.
     */
    const headTopUnknown =
      geometry.headTopSource === 'unavailable' || confidence < Math.max(uncertainBelow, 0.45);
    const { verdict, bounds } = headTopUnknown
      ? {
          verdict: { status: 'uncertain' as const, inside: false, reason: 'low_confidence' as const },
          bounds: resolveBounds(ctx.rule),
        }
      : judgeRule(ctx.rule, value, { confidence, warningMargin: 0.06 });

    const officialText = describeOfficial(bounds.official) ?? '—';
    const actualPercent = `${(value * 100).toFixed(1).replace('.', ',')} %`;
    const policyText =
      bounds.policy?.min !== undefined
        ? `${(bounds.policy.min * 100).toFixed(0)} %`
        : bounds.policy?.max !== undefined
          ? `${(bounds.policy.max * 100).toFixed(0)} %`
          : undefined;

    const message = (() => {
      if (verdict.status === 'uncertain' && verdict.reason === 'low_confidence') {
        // No number in the sentence: an unreliable measurement must not look
        // like a statement of fact. The measured value stays in `value`/`details`.
        return `Не удалось надёжно определить верхнюю границу головы (макушку), поэтому размер головы не оценён. Официальный диапазон — ${officialText}.`;
      }
      if (verdict.reason === 'within_tolerance') {
        return `Измеренная высота головы ${actualPercent} — на границе официального диапазона ${officialText}; измерение попадает в погрешность ±${(tolerance * 100).toFixed(1).replace('.', ',')} %.`;
      }
      if (verdict.reason === 'policy_below_min' || verdict.reason === 'policy_above_max') {
        return `Голова занимает ${actualPercent}: это внутри официальных ${officialText}, но выходит за наш внутренний ориентир${policyText ? ` ${policyText}` : ''}.`;
      }
      if (verdict.reason === 'above_max') {
        return `Голова занимает слишком большую часть кадра: ${actualPercent}, официальный максимум — ${officialText}.`;
      }
      if (verdict.reason === 'below_min') {
        return `Голова занимает слишком маленькую часть кадра: ${actualPercent}, официальный минимум — ${officialText}.`;
      }
      if (verdict.status === 'warning') {
        return `Размер головы ${actualPercent} — близко к границе официального диапазона ${officialText}.`;
      }
      return `Голова занимает ${actualPercent} высоты кадра — в официальном диапазоне ${officialText}.`;
    })();


    const headBox = geometry.headBox;
    const geometryShapes: NonNullable<CheckResult['geometry']> = [
      {
        type: 'rect',
        coordinates: [
          roundTo(headBox.x, 4),
          roundTo(headBox.y, 4),
          roundTo(headBox.width, 4),
          roundTo(headBox.height, 4),
        ],
      },
      {
        type: 'line',
        coordinates: [
          roundTo(geometry.faceCenterX, 4),
          roundTo(geometry.headTopPoint.y, 4),
          roundTo(geometry.faceCenterX, 4),
          roundTo(geometry.chinPoint.y, 4),
        ],
      },
      { type: 'point', coordinates: [roundTo(geometry.faceCenterX, 4), roundTo(geometry.headTopPoint.y, 4)] },
      { type: 'point', coordinates: [roundTo(geometry.faceCenterX, 4), roundTo(geometry.chinPoint.y, 4)] },
    ];

    const result = plainResult({
      id: headSizeCheck.id,
      title: headSizeCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      value: roundTo(value, 4),
      ...(bounds.official.min !== undefined ? { min: bounds.official.min } : {}),
      ...(bounds.official.max !== undefined ? { max: bounds.official.max } : {}),
      unit: 'ratio',
      message,
      requirement:
        describeOfficial(bounds.official) !== undefined
          ? `Официально: высота головы ${officialText} высоты изображения (${bounds.official.statement ?? 'источник в профиле документа'}).`
          : undefined,
      fixability: verdict.reason === 'above_max' ? 'retake' : 'fixable',
      geometry: geometryShapes,
      confidence: roundTo(confidence, 2),
      details: {
        headTopSource: geometry.headTopSource,
        headTopConfidence: geometry.headTopConfidence,
        headTopHardCases: geometry.headTopNotes ?? [],
        measurementReference: reference,
        faceHeight: geometry.faceHeight,
        headHeight: geometry.headHeight,
        headWidth: geometry.headWidth,
        headAspect: geometry.headAspect,
        margins: geometry.margins,
        verdictReason: verdict.reason ?? 'ok',
        policyViolation: verdict.policyViolation === true,
        policy: bounds.policy ?? null,
        tolerance,
        note: geometry.headTopConfidence.reason ?? null,
        displayValue: actualPercent,
      },
    });

    return withRequirement(result, ctx.rule, { reference, unit: 'ratio', tolerance });
  },
};
